import React, { useMemo } from 'react';
import moment from 'moment';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip);

export default function SubscriptionTab({
  deviceData = {},
}) {
  const {
    planName,
    statusText,
    renewalFormatted,
    warrantyFormatted,
    daysLeft,
    daysInto,
    totalDays,
  } = useMemo(() => {
    const rawExpiry = deviceData.dashboardLicensingExpiry || deviceData.dashboardValidity?.endDate;
    const rawWarranty = deviceData.warrantyExpiry || deviceData.flowmeterWarranty?.endDate;
    const rawStart = deviceData.commissioningDate || deviceData.createdAt;

    const now = moment();

    let expiryMoment = rawExpiry ? moment(rawExpiry) : null;
    if (!expiryMoment || !expiryMoment.isValid()) {
      expiryMoment = moment().add(1, 'year');
    }

    let startMoment = rawStart ? moment(rawStart) : null;
    if (!startMoment || !startMoment.isValid()) {
      startMoment = expiryMoment.clone().subtract(1, 'year');
    }

    const total = Math.max(1, expiryMoment.diff(startMoment, 'days'));
    const diffLeft = expiryMoment.diff(now, 'days');
    const left = Math.max(0, diffLeft);
    const elapsed = Math.max(0, now.diff(startMoment, 'days'));
    const into = Math.min(total, elapsed);

    let warrantyMoment = rawWarranty ? moment(rawWarranty) : null;
    const warrantyStr =
      warrantyMoment && warrantyMoment.isValid()
        ? warrantyMoment.format('DD MMM YYYY')
        : expiryMoment.clone().subtract(1, 'year').format('DD MMM YYYY');

    const status =
      (rawExpiry && moment(rawExpiry).isBefore(now)) || deviceData.active === false
        ? 'Expired'
        : 'Active';

    return {
      planName: 'Data + Compliance',
      statusText: status,
      renewalFormatted: expiryMoment.format('DD MMM YYYY'),
      warrantyFormatted: warrantyStr,
      daysLeft: left,
      daysInto: into,
      totalDays: total,
    };
  }, [deviceData]);

  const donutData = {
    datasets: [
      {
        data: [daysInto, daysLeft],
        backgroundColor: ['#1072bb', '#e6eef2'],
        borderWidth: 0,
      },
    ],
  };

  const donutOptions = {
    cutout: '72%',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
  };

  return (
    <div className="pane active">
      <div className="pane-tools">
        <h2>Subscription</h2>
      </div>

      <div className="sub-hero" style={{ marginBottom: '16px' }}>
        {/* Current Plan Gradient Card */}
        <div className="plan">
          <h3>Current plan</h3>
          <div className="big">{planName}</div>
          <div style={{ opacity: 0.9, fontSize: '13px' }}>
            {deviceData.productId || 'FM00001'} · {deviceData.productName || 'Borewell'}
          </div>

          <div className="meta">
            <div>
              <small>Status</small>
              <b style={{ color: statusText === 'Active' ? '#fff' : '#fca5a5' }}>{statusText}</b>
            </div>
            <div>
              <small>Renewal date</small>
              <b>{renewalFormatted}</b>
            </div>
            <div>
              <small>Warranty to</small>
              <b>{warrantyFormatted}</b>
            </div>
          </div>
        </div>

        {/* Days Left Ring Card */}
        <div className="client-card ring">
          <div className="donut-box" style={{ width: '150px', height: '150px', flex: 'none' }}>
            <Doughnut data={donutData} options={donutOptions} />
            <div className="donut-center">
              <b>{daysLeft}</b>
              <small>days left</small>
            </div>
          </div>

          <div>
            <div className="s-lab" style={{ color: 'var(--muted)' }}>
              Days into plan
            </div>
            <div className="s-val" style={{ fontSize: '22px', fontWeight: 800 }}>
              {daysInto} <u style={{ fontSize: '12px', color: 'var(--muted-2)' }}>/ {totalDays}</u>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
